// }
import React, { useState, useEffect, useContext, useRef } from "react";
import { PageDataContext } from "../../utils/common/PageDataContext";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  Grid,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogActions,
  Button,
  Collapse,
  MenuItem,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  Download as DownloadIcon,
  InsertDriveFile as InsertDriveFileIcon,
  DisabledByDefault as DisabledByDefaultOutlinedIcon,
  ReplyAll as ReplyAllIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import SwipeRightIcon from "@mui/icons-material/SwipeRight";
import EmailIcon from "@mui/icons-material/Email";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { externalgetApi } from "../../services/externalApis";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import { useNavigate } from "react-router-dom";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  GRN_API_01,
  GRN_API_12,
  SETTING_API_03,
  SETTING_API_09,
} from "../../api/ApiModel";
import DebitNotePdf from "../../features/documents/DebitNotePdf";
import RejectionNotePdf from "../../features/documents/RejectionNotePdf";
import GrnPdf from "../../features/documents/GrnPdf";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";

import { GRN_ROUTES } from "../../utils/sys/GrnRoutes";
import {
  DOC_CATEGORY_CODE,
  GRN_VIEW_RETURN_FILTER,
} from "../../utils/sys/GrnConstants";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReturnNotePdfHistory from "../../features/documents/ReturnNotePdfHistory";
import {
  CON_DEFAULT_PNG,
  CON_STATUS_ACTIVE,
  CON_STATUS_RETURNAPPROVED,
  CON_STATUS_RETURNSUBMITTED,CON_STATUS_RETURNREJECTED,
  CON_DEFAULT_NOIMAGE,
} from "../../utils/sys/Constants";
import ReturnNotePdf from "../../features/documents/ReturnNotePdf";
// ─── type alias ──────────────────────────────────────────────────────────────
type AnyForNow = any;

// ─── helpers ────────────────────────────────────────────────────────────────
const fmtCurrency = (val: number | null | undefined) =>
  val != null
    ? new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val)
    : "-";

const fmtNum = (val: number | null | undefined) =>
  val != null ? new Intl.NumberFormat("en-US").format(val) : "-";

// ─── reusable pieces ─────────────────────────────────────────────────────────
const SectionTitle = ({
  children,
  theme,
}: {
  children: React.ReactNode;
  theme: any;
}) => (
  <Typography
    sx={{
      color: theme.palette.text.primary,
      fontSize: theme.typography.subtitle_s,

      mb: 1,
    }}
  >
    ▸ {children}
  </Typography>
);

const InfoItem = ({
  label,
  children,
  theme,
}: {
  label: string;
  children?: React.ReactNode;
  theme: any;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
    <Typography
      sx={{
        color: theme.palette.text.primary,
        fontSize: theme.typography.subtitle_xs,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontSize: theme.typography.body_s,
      }}
    >
      {children || "-"}
    </Typography>
  </Box>
);

const Card = ({
  children,
  sx = {},
  theme,
}: {
  children: React.ReactNode;
  sx?: object;
  theme: any;
}) => (
  <Paper
    elevation={0}
    sx={{
      background: theme.palette.background.default,
      border: `0.5px solid ${theme.palette.divider}`,
      borderRadius: 2,
      mb: 1.5,
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const CardHeader = ({
  children,
  theme,
}: {
  children: React.ReactNode;
  theme: any;
}) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: "flex",
      alignItems: "center",
      gap: 0.75,
    }}
  >
    {children}
  </Box>
);

const StatCell = ({
  label,
  value,
  sub,
  theme,
}: {
  label: string;
  value: string;
  sub?: string;
  variant?: string;
  theme: any;
}) => {
  const bg = theme.palette.background.default;

  return (
    <Box sx={{ background: bg, px: 1.5, py: 1.25, flex: 1 }}>
      <Typography
        sx={{
          color: theme.palette.text.primary,
          fontSize: theme.typography.subtitle_xs,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          fontSize: theme.typography.body_s,
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          sx={{ fontSize: 10, color: theme.palette.text.secondary, mt: 0.25 }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
};

const FinRow = ({
  label,
  value,
  total,
  theme,
}: {
  label: string;
  value: string;
  bold?: boolean;
  total?: boolean;
  theme: any;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.5,
      ...(total
        ? { borderTop: `0.5px solid ${theme.palette.divider}`, mt: 0.5, pt: 1 }
        : {}),
    }}
  >
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontSize: theme.typography.subtitle_s,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontSize: theme.typography.subtitle_s,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const FinMainRow = ({
  label,
  value,
  total,
  theme,
}: {
  label: string;
  value: string;
  bold?: boolean;
  total?: boolean;
  theme: any;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      py: 0.5,
      ...(total
        ? { borderTop: `0.5px solid ${theme.palette.divider}`, mt: 0.5, pt: 1 }
        : {}),
    }}
  >
    <Typography
      sx={{
        color: theme.palette.text.primary,
        fontSize: theme.typography.subtitle_s,
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: theme.palette.text.primary,
        fontSize: theme.typography.subtitle_s,
        fontWeight: 600,
      }}
    >
      {value}
    </Typography>
  </Box>
);
const handleDownloadPdf = (name: string) => {
  const pdfUrl = `/pdf/${name}.pdf`;

  const link = document.createElement("a");
  link.href = pdfUrl;
  link.target = "_blank";
  link.download = `${name}.pdf`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const DocRow = ({
  name,
  onClick,
  onDownload,
  onPrint,
  theme,
  message,
  available,
  showDownload = true,
  showPrint = true,
}: {
  name: string;
  onClick: () => void;
  onDownload: () => void;
  onPrint: () => void;
  theme: any;
  message?: string;
  available: boolean;
  showDownload?: boolean;
  showPrint?: boolean;
}) => (
  <Box
    onClick={available ? onClick : undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1.25,
      borderBottom: `0.5px solid ${theme.palette.divider}`,
      cursor: available ? "pointer" : "not-allowed",
      opacity: available ? 1 : 0.5,
      "&:last-child": { borderBottom: "none" },
      "&:hover": available ? { background: theme.palette.action.hover } : {},
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          background: theme.palette.background.default,
          border: `0.5px solid ${theme.palette.divider}`,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 14, color: theme.palette.text.primary }} />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography sx={{ fontSize: 13, color: theme.palette.text.primary }}>
          {name}
        </Typography>

        {!available && message && (
          <Typography sx={{ fontSize: 11, color: theme.palette.text.primary }}>
            {message}
          </Typography>
        )}
      </Box>
    </Box>

    <Box sx={{ display: "flex", gap: 1 }}>
      {/* Download - only when allowed */}
      {showDownload && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
          sx={{
            width: 28,
            height: 28,
            border: `0.5px solid ${theme.palette.divider}`,
            borderRadius: 1,
            color: theme.palette.text.primary,
          }}
        >
          <DownloadIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {/* Print - only when allowed */}
      {showPrint && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPrint();
          }}
          sx={{
            width: 28,
            height: 28,
            border: `0.5px solid ${theme.palette.divider}`,
            borderRadius: 1,
            color: theme.palette.text.primary,
          }}
        >
          <LocalPrintshopIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {/* Email icon left as-is; wrap similarly if you also want it hidden for GRN Document */}
      {showPrint && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPrint();
          }}
          sx={{
            width: 28,
            height: 28,
            border: `0.5px solid ${theme.palette.divider}`,
            borderRadius: 1,
            color: theme.palette.text.primary,
          }}
        >
          <EmailIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  </Box>
);

const fmtDate = (val: string | Date | null | undefined) => {
  if (!val) return "-";

  const d = new Date(val);

  if (isNaN(d.getTime())) return "-";

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};
// ─── static fallback document list ───────────────────────────────────────────
const STATIC_DOCUMENTS = ["GRN Document", "Debit Note", "Return Note"];

// ─── main component ───────────────────────────────────────────────────────────
const GrnViewReturnPage = () => {
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE } = SYS_USER.USER_PARAM || {};
  const theme = useTheme();
  const location = useLocation();
  const locationState = location?.state || {};
  const { grncode, trsts, grnreturncode, debitnoteno } = locationState as {
    grncode?: string;
    trsts?: string | number;
    grnreturncode?: string;
    debitnoteno?: string;
  };

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [grnList, setGrnList] = useState<AnyForNow[]>([]);
  const [trackingDetails, setTrackingDetails] = useState<AnyForNow[]>([]);
  const [grnDocuments, setGrnDocuments] = useState<AnyForNow[]>([]);
  const [grnReturns, setGrnReturns] = useState<AnyForNow[]>([]);
  const [grnPaySummery, setGrnPaySummery] = useState<AnyForNow | null>(null);
  const [grnPayTax, setGrnPayTax] = useState<AnyForNow[]>([]);
  const [grnReturnList, setgrnReturnList] = useState<AnyForNow[]>([]);
  const [grnReturnMaster, setGrnReturnMaster] = useState<AnyForNow[]>([]);
  const [grnCompany, setGrnCompany] = useState<AnyForNow[]>([]);
  const [grnRejectList, setGrnRejectList] = useState<AnyForNow[]>([]);
  const [otherchargevalue, setotherchargevalue] = useState<AnyForNow[]>([]);
  const [product, setProduct] = useState<AnyForNow[]>([]);
  const [header, setHeader] = useState<AnyForNow>({});
  const [tracking, setTracking] = useState<AnyForNow[]>([]);
  const [status, setstatus] = useState<AnyForNow>([]);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  // ── PDF dialog state ──────────────────────────────────────────────────────
  const [openPdf, setOpenPdf] = useState(false);
  const [docType, setDocType] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const { setPageData } = useContext(PageDataContext);
  const [grnDocTypes, setGrnDocTypes] = useState([]);
  const navigate = useNavigate();
  // find current status based on trsts
  const currentStatus = status.find((s) => s.trsts === trsts);
  const [openDialog, setOpenDialog] = useState(false);

  const currentReturnmaster = grnReturnMaster.find(
    (s) => s.grnreturncode === grnreturncode,
  );
  const styles = useStyles(theme);
  const currentReturnItems = grnReturnList.filter(
    (item) => item.grnreturncode === grnreturncode,
  );
   const currentReturn_Items = grnReturnMaster?.filter(
    (item) => item.grnreturncode === grnreturncode,
  );
  const [anchorEl, setAnchorEl] = useState<{
    [key: number]: HTMLElement | null;
  }>({});

  
  const returnSummary = currentReturnItems.reduce(
    (acc, item) => {
      const qty = Number(item.returnedqty || 0);

      acc.taxamount += Number(item.taxamount || 0);
      acc.taxperunit += Number(item.taxperunit || 0);
      acc.totalbeforetax += Number(item.totalbeforetax || 0) * qty;
      acc.unitcostwithouttax += Number(item.unitcostwithouttax || 0) * qty;

      return acc;
    },
    {
      taxamount: 0,
      taxperunit: 0,
      totalbeforetax: 0,
      unitcostwithouttax: 0,
    },
  );

    const returnCalSummary = currentReturn_Items.reduce(
    (acc, item) => {
     

      acc.totaftertaxamount += Number(item.totaftertaxamount || 0);
      acc.tottaxableamount += Number(item.tottaxableamount || 0);
      acc.tottaxamount += Number(item.tottaxamount || 0) ;
    

      return acc;
    },
    {
      totaftertaxamount: 0,
        tottaxableamount: 0,
      tottaxamount: 0,
      
    },
  );


  console.log(returnSummary);
  const [expandedOptional, setExpandedOptional] = React.useState<{
    [key: number]: boolean;
  }>({});

  // fallback if not found
  const displayStatus = currentStatus?.trappuserremark || "Unknown Status";
  const displayColor = currentStatus?.colorcode || "#999999"; // default gray
  // Financially reused static fallback

  useEffect(() => {
    setPageData({ title: " Purchase Return History" });
  }, []);

  useEffect(() => {
    if (grncode && trsts !== undefined) {
      fetchGrnData(grncode, trsts);
    }
  }, [grncode, trsts]);

  const fetchGrnData = async (
    grncode: string,
    trsts: string | number,
  ): Promise<void> => {
    try {
      const result = await externalgetApi(GRN_API_01, "GET", {
        COMPCODE,
        GRNCODE: grncode,
        STS: trsts,
      });
      console.log("GRN Data", result);
      setGrnList(result || []);
      setTrackingDetails(result?.[0]?.tracking || []);
      setGrnReturns(result?.[0]?.returns || []);
      setGrnDocuments(result?.[0]?.documents || []);
      setGrnPaySummery(result?.[0]?.grnPaySummery || null);
      setGrnPayTax(result?.[0]?.grnPayTax || []);
      setGrnCompany(result?.[0]?.company || []);
      setgrnReturnList(result?.[0]?.returnList || []);
      setGrnReturnMaster(result?.[0]?.grnReturnMaster || []);
      setGrnRejectList(result?.[0]?.rejectList || []);
      setotherchargevalue(result?.[0]?.charges || []);
      setHeader(result?.[0]?.header || {});
      setProduct(result?.[0]?.products || []);
      setTracking(result?.[0]?.tracking || []);
    } catch (err) {
      console.error("Error fetching GRN data:", err);
    }
  };
  const fetchGrnsts = async (): Promise<void> => {
    try {
      const result = await externalgetApi(GRN_API_12, "GET", {
        STS: CON_STATUS_ACTIVE,
      });

      setstatus(result || []);
      console.log("status", result);
    } catch (err) {
      console.error("Error fetching GRN data:", err);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl((prev) => ({ ...prev, [index]: event.currentTarget }));
  };
  const handleMenuClose = (index: number) => {
    setAnchorEl((prev) => ({ ...prev, [index]: null }));
  };

  useEffect(() => {
    fetchGrnsts();
  }, []);

  useEffect(() => {
    // Fetch directly with known DOCCATCODE=1 (Item Based) to avoid category lookup mismatch.
    fetchGrnDocType(DOC_CATEGORY_CODE.ITEM_BASED);
    fetchGrnDocCategory();
  }, []);

  const fetchGrnDocCategory = async () => {
    try {
      const params = { STS: CON_STATUS_ACTIVE };
      const response = await externalgetApi(SETTING_API_09, "GET", params);
      const grnCategory = Array.isArray(response)
        ? response.find((cat) => cat.doccatname === "Item-Based Document Types")
        : null;

      if (grnCategory?.doccatcode) {
        await fetchGrnDocType(grnCategory.doccatcode);
      } else {
        // Fallback to the known working category code
        await fetchGrnDocType(DOC_CATEGORY_CODE.ITEM_BASED);
      }
      console.log("grnCategory", grnCategory);
    } catch (error) {
      console.error("Error fetching GRN document category", error);
      // Fallback to direct fetch with code 1 if category call fails
      await fetchGrnDocType(DOC_CATEGORY_CODE.ITEM_BASED);
    }
  };
  // Add this inside your component
  const getDocTypeName = (code: any) => {
    if (code === 0 || code === "0" || code === null || code === undefined) {
      return "-";
    }

    const found = grnDocTypes.find(
      (doc) => String(doc.doctypecode) === String(code),
    );

    return found ? found.doctype : "-";
  };

  const return_list = grnReturnList?.filter(
    (item) => Number(item.returnedqty) > 0,
  );

const grnReturnCounts = Object.values(
  return_list
    .filter((item) =>
      [
        String(CON_STATUS_RETURNAPPROVED),
        String(CON_STATUS_RETURNREJECTED),
      ].includes(String(item.sts))
    )
    .reduce((acc, item) => {
      const code = item.grnreturncode;

      if (!acc[code]) {
        acc[code] = {
          grnreturncode: code,
          count: 0,
        };
      }

      acc[code].count += 1;

      return acc;
    }, {} as Record<string, { grnreturncode: string; count: number }>)
);
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  // Group by grnreturncode
  const groupedReturns = return_list.reduce((acc: any, item: any) => {
    if (!acc[item.grnreturncode]) {
      acc[item.grnreturncode] = [];
    }

    acc[item.grnreturncode].push(item);

    return acc;
  }, {});

  const toggleRow = (code: string) => {
    setOpenRows((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const return_list_match = grnReturnList?.filter(
    (item) =>
      Number(item.grnreturncode) === Number(grnreturncode) &&
      Number(item.returnedqty) > 0,
  );

  const filteredProducts = product.filter((item) =>
    return_list_match?.some(
      (r) => String(r.productcode) === String(item.productcode),
    ),
  );

  const fetchGrnDocType = async (docCatCode) => {
    try {
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        DOCCATCODE: docCatCode,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(SETTING_API_03, "GET", params);
      setGrnDocTypes(response); // Save doc types to state
      console.log("grnDocTypes", response);
    } catch (error) {
      console.error("Error fetching GRN document types", error);
    }
  };

  // ── Map document name → docType label used in the Dialog ─────────────────
  const resolveDocType = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes("reject")) return "Rejection Note";
    if (lower.includes("return")) return "Return Note";
    if (lower.includes("debit")) return "Debit Note";
    if (lower.includes("grn note")) return "Debit Note";
    return "GRN Document";
  };

  const handleOpenPdf = (docName: string) => {
    setDocType(resolveDocType(docName));
    setOpenPdf(true);
  };

  const summary = grnPaySummery?.[0];
  const toggle = (key: string | number) => {
    setExpanded((prev) => {
      const isExpanded = !!prev[key];
      return isExpanded ? {} : { [key]: true };
    });
  };

  // Use documents from API if available, otherwise fall back to static list
const rejectItems = grnRejectList?.length
  ? grnRejectList
  : grnReturns;

const hasRejectItems = rejectItems?.some(
  (item) => Number(item.returnedqty) > 0
);

const documentList = [
  {
    name: "GRN Document",
    available: true,
    message: "",
    showDownload: false,
    showPrint: false,
  },
  {
    name: "Return Note",
    available: trsts === CON_STATUS_RETURNAPPROVED,
    message: hasRejectItems ? "" : "No Reject Items Available",
    showDownload: true,
    showPrint: true,
  },
  {
    name: "Debit Note",
    available: trsts === CON_STATUS_RETURNAPPROVED,
    message: hasRejectItems ? "" : "No Reject Items Available",
    showDownload: true,
    showPrint: true,
  },
].filter((doc) => doc.available);



  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState<any[]>([]);

  const handleOpenAttachments = (docpathjson: string) => {
    try {
      const docs = docpathjson ? JSON.parse(docpathjson) : [];
      setSelectedDocs(docs);
    } catch {
      setSelectedDocs([]);
    }

    setAttachmentDialogOpen(true);
  };

  const totalGRNValue = filteredProducts.reduce(
    (sum, item) => sum + Number(item.grossvalue || 0),
    0,
  );

  const handleCloseAttachments = () => {
    setAttachmentDialogOpen(false);
    setSelectedDocs([]);
  };

let imageUrl = "";

try {
  if (selectedItem?.imagepath) {
    const parsed = JSON.parse(selectedItem.imagepath);

    if (Array.isArray(parsed)) {
      imageUrl = parsed?.[0]?.FILEPATH || "";
    } else if (parsed?.FILEPATH) {
      imageUrl = parsed.FILEPATH;
    }
  }
} catch {
  // If it's not JSON, assume it's already a URL
  imageUrl = selectedItem?.imagepath || "";
}

  // 1. Add a ref to the details panel
  const detailsRef = useRef<HTMLDivElement>(null);

  // 2. Update the click handler to scroll down
  const handleStepClick = (step: any) => {
    const isSame =
      selectedStep?.endate === step.endate &&
      selectedStep?.trsts === step.trsts;
    setSelectedStep(isSame ? null : step);
    if (!isSame) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 50);
    }
  };
  const lastAction = tracking
    .filter((s) => s.trsts === trsts)
    .reduce((latest, current) => {
      return new Date(current.endate) > new Date(latest.endate)
        ? current
        : latest;
    }, tracking[0]);

  const lastActionDate = lastAction?.endate;

  const [open, setOpen] = React.useState(false);
  const [selectedStep, setSelectedStep] = React.useState(null);

  const handleOpen = (step) => {
    setSelectedStep(step);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedStep(null);
  };

  const debitNoteAmount =
  trsts !== CON_STATUS_RETURNREJECTED
    ? fmtCurrency(
        returnCalSummary.tottaxableamount +
          (header?.isvat === "Y" ? returnCalSummary.tottaxamount : 0)
      )
    : "0.00";

  return (
    <Box
      sx={{
        background: theme.palette.background.paper,
        minHeight: "100vh",
        p: 2,
      }}
    >
      {/* ── breadcrumb + actions ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
            // href=""
          >
            Home
          </Link>
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
            // href=""
          >
            GRN
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            // href="#"
            aria-current="page"
          >
            Purchases Return History
          </Link>
        </Breadcrumbs>
        {/* <Tooltip title="View GRN History" arrow>
          <IconButton
            size="small"
            onClick={() =>
              navigate(GRN_ROUTES.View_TEP_HISTORY, {
                state: {
                  grncode,
                  trsts,
                  origin: GRN_VIEW_RETURN_FILTER.ORIGIN_RETURN,
                },
              })
            }
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
            }}
          >
            <SwipeRightIcon
              sx={{
                fontSize: 20,
                color: theme.palette.primary.main,
              }}
            />
          </IconButton>
        </Tooltip> */}
      </Box>

      {/* ── GRN header card ── */}
      <Card theme={theme}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: theme.palette.text.secondary,
                  mb: 0.5,
                }}
              ></Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Return ID : {currentReturnmaster?.grnreturncode}
                </Typography>
                <Chip
                  label={displayStatus}
                  sx={{
                    backgroundColor: displayColor,
                    color: "#fff",
                    fontWeight: 400,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.subtitle_xs,
                  mt: 0.5,
                }}
              >
                Return Date: {fmtDate(currentReturnmaster?.returneddate)}
              </Typography>

              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.subtitle_xs,
                  mt: 0.5,
                }}
              >
                Supplier: {header?.suppliername}
              </Typography>
            </Box>

            <Box
             onClick={() => navigate(GRN_ROUTES.GOODS_RETURN_HISTORY_BY_ID, { state: { grncode } })}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                background: theme.palette.primary.main,
                borderRadius: 1.5,
                cursor: "pointer",
              }}
            >
              <ReplyAllIcon sx={{ fontSize: 14, color: "#fff" }} />
              <Typography
                sx={{
                  fontSize: theme.typography.body_xs,
                  color: "#fff",
                  fontWeight: 500,
                }}
              >
                Purchase Return
              </Typography>

              <Box
                sx={{
                  background: theme.palette.primary.contrastText,
                  borderRadius: "999px",
                  px: 0.75,
                  fontSize: 11,
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  lineHeight: "18px",
                }}
              >
                {grnReturnCounts.length}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* ── GRN & Supplier Info ── */}
      <Card theme={theme}>
        <Box sx={{ p: 2 }}>
          <SectionTitle theme={theme}>
            Purchases Return & References Details
          </SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              ["Return ID", currentReturnmaster?.grnreturncode],
              ["Return Date", fmtDate(currentReturnmaster?.returneddate)],
            

              ["GRN ID", grncode],
              ["GRN Date", fmtDate(header?.endate)],
              ["Original GRN Value", fmtCurrency(summary?.totgross)],
              ["Invoice No", header?.invoiceno],
              ["Invoice Date", fmtDate(header?.invoicedate)],
              ["PO Number", header?.ponumber],
              ["PO Date", fmtDate(header?.podate)],
              ["Partial Shipment", header?.ispartialship],
              ["Delivery Location", header?.restname],
              ["Return Initiated By", header?.grnenuser],
              ["Approved By", header?.grnenuser],
              ["Approved Date", fmtDate(lastActionDate)],
              ["Last Modified By", header?.grnenuser],
              ["Last Modified Date", fmtDate(lastActionDate)],
            ].map(([label, val]) => (
              <Grid item xs={6} sm={4} md={2} key={label as string}>
                <InfoItem theme={theme} label={label as string}>
                  {label === "Partial Shipment" ? (val ? "Yes" : "No") : val}
                </InfoItem>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 1.5, borderColor: theme.palette.divider }} />
          <SectionTitle theme={theme}>Supplier Information</SectionTitle>
          <Grid container spacing={1.5}>
            {[
              ["Supplier Code", header?.suprefcode],
              ["Supplier Name", header?.suppliername],
              [
                "Supplier Address",
                <Box>
                  {header?.addresslinE1 && (
                    <Typography sx={{ fontSize: theme.typography.body_xs }}>
                      {header.addresslinE1}
                    </Typography>
                  )}
                  {header?.addresslinE2 && (
                    <Typography sx={{ fontSize: theme.typography.body_xs }}>
                      {header.addresslinE2}
                    </Typography>
                  )}
                  {header?.addresslinE3 && (
                    <Typography sx={{ fontSize: theme.typography.body_xs }}>
                      {header.addresslinE3}
                    </Typography>
                  )}
                </Box>,
              ],
              ["Contact Person", header?.suppliername],
              ["Contact Phone", header?.contactno],
              ["Email", header?.email],
            ].map(([label, val]) => (
              <Grid item xs={6} sm={4} md={2} key={label as string}>
                <InfoItem theme={theme} label={label as string}>
                  {val}
                </InfoItem>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Card>

      {/* ── PO summary card ── */}
      <Card theme={theme}>
        <Box sx={{ p: 2 }}>
          <SectionTitle theme={theme}>PO Summary</SectionTitle>

          <Box
            sx={{
              display: "flex",
              border: `0.5px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <StatCell
              theme={theme}
              label="PO Number"
              value={header?.ponumber}
            />
            <StatCell
              theme={theme}
              label="PO Date"
              value={fmtDate(header?.podate)}
            />
            <StatCell theme={theme} label="PO Status" value={header?.posts} />
          </Box>
          <Card theme={theme}>
            <CardHeader theme={theme}>
              <Typography
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.subtitle_xs,
                }}
              >
                ▸ PO Items
              </Typography>
            </CardHeader>
            <TableContainer>
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow
                    sx={{ background: theme.palette.background.default }}
                  >
                    {[
                      "",
                      "Item Description",
                      "Quantity",
                      "UOM",
                      "Unit Price",
                      "Line Value",
                      "PO Value",
                      "PO Quantity",
                      "Purchased Quantity",

                      "Returned Quantity",
                      "Pending Return Quantity",
                      "Remaining Quantity",
                      "Remaining Value",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.subtitle_xs,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          py: 1,
                          px: 1.5,
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                          width: h === "" ? 36 : undefined,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map((item) => {
                    const returnItem = return_list?.find(
                      (r) =>
                        r.productcode === item.productcode &&
                        String(r.sts) === CON_STATUS_RETURNAPPROVED,
                    );

                    // Place this inside the product.map() where returnItem is already defined:

                    return (
                      <React.Fragment key={item.id}>
                        <TableRow
                          onClick={() => toggle(item.id)}
                          sx={{
                            cursor: "pointer",
                            background: expanded[item.id]
                              ? theme.palette.action.hover
                              : theme.palette.background.default,
                            "&:hover td": {
                              background: theme.palette.action.hover,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              px: 1,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          ></TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: theme.typography.subtitle_xs,
                              }}
                            >
                              {item.productname}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: theme.typography.subtitle_xs,
                              color: theme.palette.text.secondary,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {item.acceptedqty}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: theme.typography.subtitle_xs,
                              color: theme.palette.text.secondary,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {item.immeasuringunit}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: theme.typography.subtitle_xs,
                              color: theme.palette.text.secondary,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {fmtNum(item.unitprice)}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: theme.typography.subtitle_xs,
                              color: theme.palette.text.secondary,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {fmtNum(item.grossvalue)}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              fontSize: theme.typography.subtitle_xs,
                              color: theme.palette.text.secondary,

                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {fmtNum(item.grossvalue)}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.subtitle_xs,
                              fontWeight: 600,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {item.poqty}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.subtitle_xs,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {item.receivedqty}
                          </TableCell>{" "}
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.subtitle_xs,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {(() => {
                              const total = return_list
                                ?.filter(
                                  (r) =>
                                    String(r.productcode) ===
                                      String(item.productcode) &&
                                    String(r.sts) === CON_STATUS_RETURNAPPROVED,
                                )
                                ?.reduce(
                                  (sum, r) => sum + Number(r.returnedqty ?? 0),
                                  0,
                                );
                              return total > 0 ? fmtNum(total) : "-";
                            })()}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.subtitle_xs,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {(() => {
                              const totalReturned = return_list
                                ?.filter(
                                  (r) =>
                                    String(r.productcode) ===
                                      String(item.productcode) &&
                                    String(r.sts) === CON_STATUS_RETURNAPPROVED,
                                )
                                ?.reduce(
                                  (sum, r) => sum + Number(r.returnedqty ?? 0),
                                  0,
                                );

                              return (
                                Number(item.acceptedqty || 0) -
                                Number(totalReturned || 0)
                              );
                            })()}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.subtitle_xs,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {Number(item.acceptedqty || 0) -
                              Number(returnItem?.returnedqty || 0)}
                          </TableCell>
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 1,
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.subtitle_xs,
                              borderBottom: `0.5px solid ${theme.palette.divider}`,
                            }}
                          >
                            {(() => {
                              const totalReturned = return_list
                                ?.filter(
                                  (r) =>
                                    String(r.productcode) ===
                                      String(item.productcode) &&
                                    String(r.sts) === CON_STATUS_RETURNAPPROVED,
                                )
                                ?.reduce(
                                  (sum, r) => sum + Number(r.returnedqty ?? 0),
                                  0,
                                );

                              const remainingQty =
                                Number(item.acceptedqty || 0) -
                                Number(totalReturned || 0);
                              const total =
                                remainingQty * Number(item.unitprice || 0);

                              return fmtCurrency(total);
                            })()}
                          </TableCell>
                        </TableRow>

                        {/* expanded row */}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Box
            sx={{
              display: "flex",
              border: `0.5px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <StatCell
              theme={theme}
              label="Total  Value"
              value={fmtCurrency(totalGRNValue)}
            />
          </Box>
        </Box>
      </Card>

      <Card theme={theme}>
        <Box sx={{ p: 2 }}>
          <SectionTitle theme={theme}> Return & Delivery Details</SectionTitle>
          <Grid container spacing={1.5}>
            {[
              ["Dispatched Number", header?.dispatchno],
              ["Dispatched Date", fmtDate(header?.dispatchedate)],
              ["Debit Note No", debitnoteno],
              ["Debit Note Date", fmtDate(header?.deliverynotedate)],
              ["Delivery Location", header?.restname],
              ["AWB Number", header?.awbno],
              ["Bill of Lading Number", header?.blno],
              ["Partial Shipment", header?.ispartialship],
            ].map(([label, val]) => (
              <Grid item xs={6} sm={4} md={3} key={label as string}>
                <InfoItem theme={theme} label={label as string}>
                  {label === "Partial Shipment" ? (val ? "Yes" : "No") : val}
                </InfoItem>
              </Grid>
            ))}
          </Grid>
        </Box>{" "}
      </Card>

      {/* ── Purchased Items table ── */}

      {/* ── Financial Summary ── */}
      <Card theme={theme}>
        <CardHeader theme={theme}>
          <SectionTitle theme={theme}> Return Items</SectionTitle>
        </CardHeader>
        <TableContainer>
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ background: theme.palette.background.default }}>
                {[
                  "",
                  "Item Description",
                  "Batch Number",
                  "UOM",
                  "GRN Quantity ",
                  "Returned Quantity",
                  "Available Quantity",
                  "Unit Cost",
                  "Return Value",
                  "Actions",
                ].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      // fontSize: 10,
                      fontWeight: 600,
                      color: theme.palette.text.primary,

                      fontSize: theme.typography.subtitle_xs,

                      py: 1,
                      px: 1.5,
                      borderBottom: `0.5px solid ${theme.palette.divider}`,
                      width: h === "" ? 36 : undefined,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((item, index) => {
              const rejectItem =
  grnRejectList?.length > 0
    ? grnRejectList.find(
        (r) => r.productcode === item.productcode
      )
    : grnReturns?.find(
        (r) => r.productcode === item.productcode
      );
                const returnItem = grnReturns
                  ?.filter(
                    (r) =>
                      String(r.productcode) === String(item.productcode) &&
                      Number(r.returnedqty) > 0 &&
                      String(r.grnreturncode) === String(grnreturncode),
                  )
                  .sort(
                    (a, b) => Number(b.returnedqty) - Number(a.returnedqty),
                  )?.[0];

                const ReturnedQty = return_list
                  ?.filter(
                    (r) =>
                      String(r.productcode) === String(item.productcode) &&
                      String(r.grnreturncode) === String(grnreturncode),
                  )
                  ?.reduce((sum, r) => sum + Number(r.returnedqty ?? 0), 0);

                const totalReturnedQty = return_list
                    ?.filter(
                                              (r) =>
                                                String(r.productcode) ===
                                                  String(item.productcode) &&
                                                 [CON_STATUS_RETURNAPPROVED, CON_STATUS_RETURNSUBMITTED].includes(String(r.sts))
                                            )
                  ?.reduce((sum, r) => sum + Number(r.returnedqty ?? 0), 0);

                const totalReturnedQtyvalue = (return_list ?? [])
  .filter(
    (r) =>
      String(r.productcode) === String(item.productcode) &&
      String(r.grnreturncode) === String(grnreturncode),
  )
  .reduce((sum, r) => {
    const base = Number(r.totalbeforetax || 0);
    const tax = header?.isvat === "Y" ? Number(r.taxamount || 0) : 0;

    return sum + base ;
  }, 0);

                const rowKey = item.productcode ?? item.id;
                return (
                  <React.Fragment key={item.id}>
                    <TableRow
                      onClick={() => toggle(rowKey)}
                      key={index}
                      sx={{
                        cursor: "pointer",
                        background: theme.palette.background.default,
                      }}
                    >
                      <TableCell
                        sx={{
                          px: 1,
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                        <IconButton size="small">
                          {expanded[rowKey] ? (
                            <ExpandLess
                              sx={{
                                fontSize: 16,
                                color: theme.palette.text.secondary,
                              }}
                            />
                          ) : (
                            <ExpandMore
                              sx={{
                                fontSize: 16,
                                color: theme.palette.text.secondary,
                              }}
                            />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: theme.typography.body_xs,
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.productname}
                          <Typography
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.body_xs,
                              mt: 0.2,
                            }}
                          >
                            Item Code: {item.productcode}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: theme.typography.body_xs,
                              color: theme.palette.text.secondary,
                              mt: 0.2,
                            }}
                          >
                            Brand: {item.brandname}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: theme.typography.body_xs,
                              color: theme.palette.text.secondary,
                              mt: 0.2,
                            }}
                          >
                            Model Name:{" "}
                            {item?.modelname && item.modelname !== "N/A"
                              ? item.modelname
                              : "-"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: theme.typography.body_xs,
                              color: theme.palette.text.secondary,
                              mt: 0.2,
                            }}
                          >
                            Variant:{" "}
                            {item?.modelname && item.modelname !== "N/A"
                              ? item.modelname
                              : "-"}
                          </Typography>
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,

                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                        {item.batchno || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                        {item.immeasuringunit}
                      </TableCell>

                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                      {fmtNum(Number(item.acceptedqty) + Number(item.freeqty))} 
                      </TableCell>
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                        {fmtNum(ReturnedQty)}
                      </TableCell>

                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,
                      
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
                        {item.acceptedqty - totalReturnedQty}
                      </TableCell>
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,
                        
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
{fmtCurrency(
  (Number(item.perunitaftertaxvalue) || 0) +
  (Number(item.perunitchargevalue) || 0)
)}
                      </TableCell>
                      <TableCell
                        sx={{
                          px: 1.5,
                          py: 1,
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body_s,
                        
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                        }}
                      >
{fmtCurrency(
  (
    (Number(item?.perunittaxableamount) || 0) + (header?.isvat === "Y" ? Number(item?.perunittaxvalue) : 0) +
    (Number(item?.perunitchargevalue) || 0)
  ) * (Number(ReturnedQty) || 0)
)}
                      </TableCell>
                      <TableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          borderBottom: `0.5px solid ${theme.palette.divider}`,
                          verticalAlign: "middle",
                          width: 70,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            width: "100%",
                            height: "100%",
                            pl: 1,
                          }}
                        >
                          {/* Three-dots menu */}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent row expand
                              handleMenuOpen(e, index);
                            }}
                          >
                            <MoreVertIcon fontSize="small"  sx={{ mr: 1.5 }} />
                          </IconButton>

                          <Menu
                            anchorEl={anchorEl[index]}
                            open={Boolean(anchorEl[index])}
                            onClose={() => handleMenuClose(index)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setOpenDialog(true);
                              }}
                            >
                              <AddPhotoAlternateIcon fontSize="small"  sx={{ mr: 1.5 }}/>
                              Item Image
                            </MenuItem>

                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();

                                handleMenuClose(index);

                                navigate(GRN_ROUTES.View_TEP_HISTORY, {
                                  state: {
                                    grncode,
                                    trsts,
                                    trcatcode:"2", // hardcoded for return history
                                    origin:
                                      GRN_VIEW_RETURN_FILTER.ORIGIN_RETURN,
                                  },
                                });
                              }}
                            >
                              <VisibilityOutlinedIcon fontSize="small"  sx={{ mr: 1.5 }}/>
                              View GRN
                            </MenuItem>

                            <Divider sx={{ my: 0.5 }} />

                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();

                                handleMenuClose(index);

                                handleOpenAttachments(item.docpathjson);
                              }}
                            >
                              <AttachFileOutlinedIcon fontSize="small" />
                              Attachments
                            </MenuItem>
                          </Menu>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* expanded row */}
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, border: "none" }}>
                        <Collapse
                          in={!!expanded[rowKey]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              background: theme.palette.background.paper,
                              px: 3,
                              py: 0,
                            }}
                          >
                            <Box
                              sx={{
                                py: 1.5,
                                borderBottom: `0.5px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  mb: 1,
                                }}
                              >
                                Return Items
                              </Typography>
                              <Box
                                sx={{ display: "flex", gap: 2, width: "100%" }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Return Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {returnItem?.returnedqty ?? 0}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Reason
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,

                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {returnItem?.reason?.trim() || "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Remark
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {returnItem?.descrip?.trim() || "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Attachments
                                  </Typography>

                                  {(() => {
                                    let attachments: any[] = [];

                                    try {
                                      attachments = returnItem?.docpathjson
                                        ? JSON.parse(returnItem.docpathjson)
                                        : [];
                                    } catch {
                                      attachments = [];
                                    }

                                    const validAttachments = attachments.filter(
                                      (att: any) =>
                                        att.FILEPATH &&
                                        !att.FILEPATH.includes(CON_DEFAULT_PNG),
                                    );

                                    return validAttachments.length > 0 ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 0.5,
                                          flexWrap: "wrap",
                                          mt: 0.25,
                                        }}
                                      >
                                        {validAttachments.map(
                                          (att: any, i: number) => (
                                            <Box
                                              key={i}
                                              component="a"
                                              href={att.FILEPATH}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 11,
                                                color:
                                                  theme.palette.primary.main,
                                                textDecoration: "none",
                   filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",
                                    }}
                                            >
                                                <InsertDriveFileIcon
                                                                                             sx={{ fontSize: 16, color: "#170202" }}
                                                                                           />
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    ) : (
                                         <Box
                                                          component="a"
                                                        
                                                          sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            fontSize: theme.typography.subtitle_xs,
                                                           
                                                            color: theme.palette.text.primary,
                                                            textDecoration: "none",
                                                              filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",
  }}
                                                         
                                                        >
                                                      <img
                                                    
                                      src={CON_DEFAULT_NOIMAGE}
                                      alt=""
                                      height={15}
                                      width={15}
                                      
                                    /> </Box>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                py: 1.5,
                                borderBottom: `0.5px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  mb: 1,
                                }}
                              >
                                Additional Details
                              </Typography>
                              <Box
                                sx={{ display: "flex", gap: 2, width: "100%" }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Batch No
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                  {item?.batchno && item.batchno !== "N/A" ? item.batchno : "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Model Name
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item?.modelname && item.modelname !== "N/A"
                                      ? item.modelname
                                      : "-"}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Generic Name
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item?.modelname && item.modelname !== "N/A"
                                      ? item.modelname
                                      : "-"}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    MFD
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item?.manufacdate &&
                                    item.manufacdate !== "0001-01-01T00:00:00"
                                      ? fmtDate(item.manufacdate)
                                      : "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    EXP
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item?.expdate &&
                                    item.expdate !== "0001-01-01T00:00:00"
                                      ? fmtDate(item.expdate)
                                      : "-"}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box>
                                <Box
                                  sx={{
                                    py: 1.5,
                                    borderTop: `0.5px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                      mb: 1,
                                    }}
                                  >
                                    Item-Level Financial
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 2,
                                      width: "100%",
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Unit Cost
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                      { fmtCurrency(
  Number(item?.perunitaftertaxvalue ?? 0) +
  Number(item?.perunitchargevalue ?? 0)
)}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Discount (item-level)
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {item?.discountrate || "-"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Tax Rate (%)
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {item?.taxrate || "-"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Tax Value
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {fmtCurrency(item?.taxvalue) || "-"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Tax Per Unit
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {fmtCurrency(item?.perunittaxvalue) ??
                                          "-"}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.secondary,
                                        }}
                                      >
                                        Net Value
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: theme.typography.body_xs,
                                          color: theme.palette.text.primary,
                                        }}
                                      >
                                        {fmtCurrency(
  (Number(item.acceptedqty ?? 0) + Number(item.freeqty ?? 0)) *
  (
    Number(item?.perunitaftertaxvalue ?? 0) +
    Number(item?.perunitchargevalue ?? 0)
  )
)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>{" "}
                            </Box>

                            <Box
                              sx={{
                                py: 1.5,
                                borderBottom: `0.5px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Box
                                sx={{ display: "flex", gap: 2, width: "100%" }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Original GRN Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item?.acceptedqty +
                                      Number(item?.freeqty) || "-"}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Previous Return Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
{(() => {
  const approvedReturns = (return_list || []).filter(
    (r: AnyForNow) =>
      String(r.productcode) === String(item.productcode) &&
      String(r.sts) === String(CON_STATUS_RETURNAPPROVED) &&
      String(r.grnreturncode) !== String(grnreturncode) // Exclude current return
  );

  if (approvedReturns.length === 0) return "-";

  // Get the latest approved return before the current one
  const previousApproved = approvedReturns.reduce((latest, current) =>
    Number(current.grnreturncode) > Number(latest.grnreturncode)
      ? current
      : latest
  );

  return fmtNum(previousApproved.returnedqty);
})()}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Current Returned Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                 
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {returnItem?.returnedqty || "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Pending Return Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                 {(() => {
  const pendingApprovalQty = (return_list || [])
    .filter(
      (r: AnyForNow) =>
        String(r.productcode) === String(item.productcode) &&
        String(r.sts) === String(CON_STATUS_RETURNSUBMITTED)
    )
    .reduce(
      (sum: number, r: AnyForNow) =>
        sum + Number(r.returnedqty ?? 0),
      0
    );

  return pendingApprovalQty === 0
    ? "-"
    : fmtNum(pendingApprovalQty);
})()}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Available / Returnable Quantity
                                  </Typography>

                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                     {(() => {
                                                             const totalReturned = return_list
                                                               ?.filter(
                                                                 (r) =>
                                                                   String(r.productcode) ===
                                                                     String(item.productcode) &&
                                                                    [CON_STATUS_RETURNAPPROVED, CON_STATUS_RETURNSUBMITTED].includes(String(r.sts))
                                                               )
                                                               ?.reduce(
                                                                 (sum, r) => sum + Number(r.returnedqty ?? 0),
                                                                 0,
                                                               );
                                   
                                                             return fmtNum(
                                                               Number(item.acceptedqty || 0) -
                                                                 Number(totalReturned || 0) ,
                                                             );
                                                           })()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* ── Reject Items Section ── */}
                            <Box
                              sx={{
                                py: 1.5,
                                borderBottom: `0.5px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  mb: 1,
                                }}
                              >
                                Reject Items
                              </Typography>
                              <Box
                                sx={{ display: "flex", gap: 2, width: "100%" }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Reject Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {rejectItem?.returnedqty || "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Reason
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                    
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {rejectItem?.reason?.trim() || "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Remark
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {rejectItem?.descrip?.trim() || "-"}
                                  </Typography>
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Attachments
                                  </Typography>

                                  {(() => {
                                    let attachments: any[] = [];

                                    try {
                                      attachments = rejectItem?.docpathjson
                                        ? JSON.parse(rejectItem.docpathjson)
                                        : [];
                                    } catch {
                                      attachments = [];
                                    }

                                    const validAttachments = attachments.filter(
                                      (att: any) =>
                                        att.FILEPATH &&
                                        !att.FILEPATH.includes(CON_DEFAULT_PNG),
                                    );

                                    return validAttachments.length > 0 ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 0.5,
                                          flexWrap: "wrap",
                                          mt: 0.25,
                                        }}
                                      >
                                        {validAttachments.map(
                                          (att: any, i: number) => (
                                            <Box
                                              key={i}
                                              component="a"
                                              href={att.FILEPATH}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 11,
                                                color:
                                                  theme.palette.text.primary,
                                                textDecoration: "none",
                                                                                                              filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",

                                              }}
                                            >
                                                <InsertDriveFileIcon
                                                                                             sx={{ fontSize: 16, color: "#170202" }}
                                                                                           />
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    ) : (
                                     <Box
                                                          component="a"
                                                        
                                                          sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            fontSize: theme.typography.subtitle_xs,
                                                           
                                                            color: theme.palette.text.primary,
                                                            textDecoration: "none",
                                                              filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",
  }}
                                                         

                                                          
                                                        >
                                                      <img
                                                    
                                      src={CON_DEFAULT_NOIMAGE}
                                      alt=""
                                      height={15}
                                      width={15}
                                      
                                    /> </Box>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            </Box>

                            {/* ── FOC Items Section ── */}
                            {/* ── FOC Items Section ── */}
                            <Box
                              sx={{
                                py: 1.5,
                                borderBottom: `0.5px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: theme.typography.body_xs,
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  mb: 1,
                                }}
                              >
                                FOC Items
                              </Typography>

                              <Box
                                sx={{ display: "flex", gap: 2, width: "100%" }}
                              >
                                {/* FOC Quantity */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    FOC Quantity
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item.freeqty || "-"}
                                  </Typography>
                                </Box>

                                {/* Remark */}
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    Remark
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: theme.typography.body_xs,
                                      color: theme.palette.text.primary,
                                    }}
                                  >
                                    {item.descrip?.trim() || "-"}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* ── Attachments Section ── */}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
          },
          gap: 2,
          p: 2,
        }}
      >
        {/* Left Box - Documents */}
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            overflow: "hidden",
          }}
        >
          {/* ── Documents ── */}

          <CardHeader theme={theme}>
            <Typography
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.subtitle_xs,

                letterSpacing: "0.06em",
              }}
            >
              ▸ Documents
            </Typography>
          </CardHeader>
{documentList.map((doc) => (
  <DocRow
    key={doc.name}
    theme={theme}
    name={doc.name}
    available={doc.available}
    message={doc.message}
    showDownload={doc.showDownload}
    showPrint={doc.showPrint}
    onClick={() => handleOpenPdf(doc.name)}
    onDownload={() => handleDownloadPdf(doc.name)}
    onPrint={() => handleOpenPdf(doc.name)}
  />
))}
        </Box>
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            p: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontSize: theme.typography.subtitle_m,

              mb: 1,
            }}
          >
            Financial Summary
          </Typography>
          {/* 
          // <FinRow theme={theme} label="Currency" value="" />
 totaftertaxamount: 0,
        tottaxableamount: 0,
      tottaxamount: 0,
          // <FinRow theme={theme} label="Exchange Rate" value="" /> */}

          <FinRow
            theme={theme}
            label="Gross Return Amount"
            value={fmtCurrency(returnCalSummary.tottaxableamount || 0.0)}
          />

          

   

          <FinRow
            theme={theme}
            label="Tax "
            value={fmtCurrency(returnCalSummary.tottaxamount)}
          />


         <FinMainRow
  theme={theme}
  label="Net Return Value"
  value={fmtCurrency(
    returnCalSummary.tottaxableamount + 
      (header?.isvat === "Y" ? returnCalSummary.tottaxamount : 0)
  )}
  bold
/>    
        <FinMainRow
  theme={theme}
  label="Debit Note Amount"
  value={debitNoteAmount}
/>
        </Box>{" "}
      </Box>
      {/* ── GRN Tracking ── */}

      <Box
        sx={{
          border: `2px solid ${theme.palette.divider}`,
          borderRadius: 2,

          mt: 3,
        }}
      >
        <Paper sx={{ p: 2, mb: 0, overflow: "hidden" }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              mb: 2,
              textTransform: "capitalize",
            }}
          >
            • GRN Tracking Details
          </Typography>

          {/* Timeline row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              overflowX: "auto",
              py: 1,
            }}
          >
            {tracking.map((step, index) => {
              const tracksts =
                status.find((s) => s.trsts === step.trsts)?.trenuserremark ||
                "";
              const isSelected =
                selectedStep?.endate === step.endate &&
                selectedStep?.trsts === step.trsts;

              return (
                <React.Fragment key={index}>
                  <Box sx={{ textAlign: "center", flex: 1 }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ fontSize: 11 }}>
                          <div>
                            <b>User:</b> {step.enuser}
                          </div>
                          <div>
                            <b>Status:</b> {tracksts}
                          </div>
                          <div>
                            <b>Date:</b> {fmtDate(step.endate)}
                          </div>
                          <div>
                            <b>Time:</b>{" "}
                            {new Date(step.endate).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </div>
                        </Box>
                      }
                    >
                      <Box
                        onClick={() => handleStepClick(step)}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background:
                            index === tracking.length - 1
                              ? "#eb6a1f"
                              : "#28A745",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: theme.typography.subtitle_xs,
                          margin: "0 auto",
                          cursor: "pointer",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          // ── highlight when selected ──
                          ...(isSelected && {
                            outline: "3px solid #887e7e",
                            outlineOffset: "2px",
                            boxShadow: "0 0 0 5px rgba(245, 242, 241, 0.2)",
                            transform: "scale(1.18)",
                          }),
                          "&:hover": {
                            transform: isSelected
                              ? "scale(1.18)"
                              : "scale(1.12)",
                            boxShadow: "0 0 0 4px rgba(235,106,31,0.15)",
                          },
                        }}
                      >
                        ✓
                      </Box>
                    </Tooltip>
                    <Typography sx={{ fontSize: 10, mt: 0.5 }}>
                      S{index + 1}
                    </Typography>
                  </Box>

                  {index !== tracking.length - 1 && (
                    <Box
                      sx={{
                        height: 2,
                        flex: 1,
                        background: theme.palette.divider,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>

          {/* ── Inline details panel (no Dialog) ── */}
          {selectedStep && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Box
                ref={detailsRef}
                sx={{
                  width: "50%", // ← adjust % as needed
                  p: 1.5,

                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.background.default,
                  fontSize: 14,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, fontWeight: 600 }}
                  ></Typography>
                  <Box
                    onClick={() => setSelectedStep(null)}
                    sx={{
                      cursor: "pointer",
                      fontSize: 16,
                      lineHeight: 1,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    ✕
                  </Box>
                </Box>

                <table
                  style={{
                    width: "100%",
                    fontSize: 14,
                    borderCollapse: "collapse",
                  }}
                >
                  <tbody>
                    {[
                      {
                        label: "Status",
                        value:
                          status.find((s) => s.trsts === selectedStep.trsts)
                            ?.trenuserremark || "-",
                      },
                      { label: "Date", value: fmtDate(selectedStep.endate) },
                      {
                        label: "Time",
                        value: new Date(selectedStep.endate).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        ),
                      },
                      {
                        label: "Updated User",
                        value: selectedStep.enuser || "-",
                      },
                      { label: "Reason", value: selectedStep.reason || "-" },
                      { label: "Remark", value: selectedStep.remark || "-" },
                    ].map(({ label, value }) => (
                      <tr key={label}>
                        <td
                          style={{
                            fontWeight: 600,
                            padding: "5px 4px",
                            width: "40%",
                            color: "inherit",
                          }}
                        >
                          {label}
                        </td>
                        <td style={{ padding: "5px 4px" }}>{value}</td>
                      </tr>
                    ))}

                    {/* Attachment row */}
                    <tr>
                      <td
                        style={{
                          fontWeight: 600,
                          padding: "5px 4px",
                          verticalAlign: "top",
                        }}
                      >
                        Attachment
                      </td>
                      <td style={{ padding: "5px 4px" }}>
                        {(() => {
                          if (!selectedStep?.docpaths) return "-";
                          let docs = [];
                          try {
                            docs = JSON.parse(selectedStep.docpaths);
                          } catch {
                            return "-";
                          }
                          const validDocs = docs.filter(
                            (d) => d.FILEPATH && d.FILEPATH !== CON_DEFAULT_PNG,
                          );
                          if (validDocs.length === 0) return "-";
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {validDocs.map((doc, i) => (
                                <Box
                                  key={i}
                                  component="a"
                                  href={doc.FILEPATH}
                                  target="_blank"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    fontSize: theme.typography.subtitle_xs,
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    textDecoration: "none",
                                               filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",
                                   
  }}
                                 
                                >
                                 <InsertDriveFileIcon
                                                                              sx={{ fontSize: 16, color: "#170202" }}
                                                                            />
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={attachmentDialogOpen}
        onClose={handleCloseAttachments}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          Attachments
        </DialogTitle>

        <DialogContent dividers>
          {selectedDocs.length > 0 ? (
            selectedDocs.map((doc: any, i: number) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 2,
                  width: "100%",
                  mb: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                {/* Document Type */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.body_xs,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Document Type
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: theme.typography.body_xs,
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {getDocTypeName(doc.doctypecode || doc.DOCTYPECODE)}
                  </Typography>
                </Box>

                {/* Remark */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.body_xs,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Remark
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: theme.typography.body_xs,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {doc.REMARK || "-"}
                  </Typography>
                </Box>

                {/* Attachment */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: theme.typography.body_xs,
                      color: theme.palette.text.secondary,
                      mb: 0.5,
                    }}
                  >
                    Attachment
                  </Typography>

                  {doc.FILEPATH && doc.FILEPATH !== CON_DEFAULT_PNG ? (
                    <Box
                      component="a"
                      href={doc.FILEPATH}
                      target="_blank"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: theme.typography.body_xs,
                        fontWeight: 600,

                        color: theme.palette.text.primary,
                        textDecoration: "none",
                                   filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",
                      }}
                    >
                        <InsertDriveFileIcon
                                                                     sx={{ fontSize: 16, color: "#170202" }}
                                                                   />
                    </Box>
                  ) : (
                        <Box
                                                          component="a"
                                                        
                                                          sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            fontSize: theme.typography.subtitle_xs,
                                                           
                                                            color: theme.palette.text.primary,
                                                            textDecoration: "none",
                                                              filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none",
  }}
                                                         
                                                        >
                                                      <img
                                                    
                                      src={CON_DEFAULT_NOIMAGE}
                                      alt=""
                                      height={15}
                                      width={15}
                                      
                                    /> </Box>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                fontSize: theme.typography.body_xs,
                color: "text.secondary",
              }}
            >
              No attachments
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            sx={{
              textTransform: "capitalize",
              border: "1px solid",
              borderColor: "divider",
            }}
            onClick={handleCloseAttachments}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── PDF Preview Dialog ── */}
      <Dialog
        open={openPdf}
        onClose={() => setOpenPdf(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {docType} Preview
          <IconButton
            aria-label="close"
            onClick={() => setOpenPdf(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <DisabledByDefaultOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {docType === "Return Note" ? (
            <ReturnNotePdf
              grnHeader={header}
              grnItems={product}
              grnPaySummery={grnPaySummery}
              docType={docType}
              grnReturns={grnReturns}
              grnCompany={grnCompany}
              grnReturnList={grnReturnList}
              grnReturnMaster={grnReturnMaster}
              returnedProducts={grnReturnList}
              grnreturncode={grnreturncode}
            />
          ) : docType === "Rejection Note" ? (
            <RejectionNotePdf
              grnHeader={header}
              grnItems={product}
              docType={docType}
              grnPaySummery={grnPaySummery}
              grnReturns={grnReturns}
              grnRejectList={grnRejectList}
              grnCompany={grnCompany}
            />
          ) : docType === "Debit Note" ? (
            <DebitNotePdf
              grnHeader={header}
              grnItems={product}
              grnPaySummery={grnPaySummery}
              docType={docType}
              grnReturns={grnReturns}
              grnCompany={grnCompany}
              grnReturnList={grnReturnList}
              grnReturnMaster={grnReturnMaster}
              returnedProducts={grnReturnList}
              grnreturncode={grnreturncode}
              debitnoteno={debitnoteno}
            />
          ) : (
            <GrnPdf
              grnHeader={header}
              grnItems={product}
              grnPaySummery={grnPaySummery}
              grnReturns={grnReturns}
              docType={docType}
              grnCompany={grnCompany}
              grnReturnList={grnReturnList}
              grnReturnMaster={grnReturnMaster}
              returnedProducts={grnReturnList}
                 grnRejectList={grnRejectList}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openReturnDialog}
        onClose={() => setOpenReturnDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Purchase Return History
          <IconButton
            onClick={() => setOpenReturnDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <DisabledByDefaultOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Table
            size="small"
            sx={{
              borderCollapse: "separate",
              borderSpacing: "0 10px",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell width={60}></TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    borderBottom: "none",
                    fontSize: 13,
                  }}
                >
                  Return ID
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    borderBottom: "none",
                    fontSize: 13,
                  }}
                >
                  Status
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Object.entries(groupedReturns).map(
                ([grnreturncode, items]: any) => {
                  const firstRow = items[0];

                  const currentSts = status.find(
                    (s: any) =>
                      s.trsts?.toString() === firstRow.sts?.toString(),
                  );

                  const label = currentSts?.trappuserremark || "Unknown";

                  const color = currentSts?.colorcode || "#94a3b8";

                  const totalValue = items.reduce(
                    (sum: number, item: any) =>
                      sum +
                      (Number(item.totalbeforetax || 0) +
                        Number(item.taxamount || 0)),
                    0,
                  );

                  return (
                    <React.Fragment key={grnreturncode}>
                      {/* Main Row */}
                      <TableRow
                        sx={{
                          "& td": {
                            borderBottom: "none",
                            background: theme.palette.background.default,
                          },

                          "& td:first-of-type": {
                            borderTopLeftRadius: 14,
                            borderBottomLeftRadius: 14,
                          },

                          "& td:last-of-type": {
                            borderTopRightRadius: 14,
                            borderBottomRightRadius: 14,
                          },

                          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
                        }}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRow(grnreturncode)}
                            sx={{
                              border: `0.5px solid ${theme.palette.divider}`,
                              background: theme.palette.background.default,

                              "&:hover": {
                                background: theme.palette.action.hover,
                              },
                            }}
                          >
                            {openRows[grnreturncode] ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>

                        <TableCell>
                          <Box>
                            <Box
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                fontSize: 14,
                              }}
                            >
                              {grnreturncode}
                            </Box>

                            <Box
                              sx={{
                                fontSize: 11,
                                color: theme.palette.text.secondary,
                                mt: 0.3,
                              }}
                            ></Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "999px",
                              fontSize: 11,
                              fontWeight: 700,
                              background: `${color}15`,
                              color: color,
                              border: `1px solid ${color}30`,
                            }}
                          >
                            {label}
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expand Section */}
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            borderBottom: "none",
                            py: 0,
                            px: 0,
                          }}
                        >
                          <Collapse
                            in={openRows[grnreturncode]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                mx: 2,
                                mb: 2,
                                mt: 1,
                                borderRadius: 3,
                                overflow: "hidden",
                                border: `0.5px solid ${theme.palette.divider}`,
                                background: theme.palette.background.default,
                              }}
                            >
                              {/* Summary */}

                              {/* Inner Table */}
                              <Table size="small">
                                <TableHead>
                                  <TableRow
                                    sx={{
                                      background:
                                        theme.palette.background.default,
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        fontWeight: 700,
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      Product
                                    </TableCell>

                                    <TableCell
                                      sx={{
                                        fontWeight: 700,
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      Quantity
                                    </TableCell>

                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: 700,
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      Value
                                    </TableCell>
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {items.map((item: any) => (
                                    <TableRow
                                      key={item.grnreturndetcode}
                                      hover
                                      sx={{
                                        "&:last-child td": {
                                          borderBottom: "none",
                                        },
                                      }}
                                    >
                                      <TableCell>
                                        <Box>
                                          <Box
                                            sx={{
                                              fontWeight: 600,
                                              color: theme.palette.text.primary,
                                            }}
                                          >
                                            {item.productname}
                                          </Box>

                                          <Box
                                            sx={{
                                              fontSize: 11,
                                              color:
                                                theme.palette.text.secondary,
                                              mt: 0.2,
                                            }}
                                          >
                                            {item.reason}
                                          </Box>
                                        </Box>
                                      </TableCell>

                                      <TableCell>
                                        <Box
                                          sx={{
                                            fontWeight: 600,
                                          }}
                                        >
                                          {item.returnedqty}
                                        </Box>
                                      </TableCell>

                                      <TableCell align="right">
                                        <Box
                                          sx={{
                                            fontWeight: 700,
                                            color: theme.palette.text.secondary,
                                          }}
                                        >
                                         {fmtCurrency(
  Number(item.totalbeforetax || 0) +
    (header?.isvat === "Y" ? Number(item.taxamount || 0) : 0)
)}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                },
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Item Image
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <DisabledByDefaultOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Preview */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f7f7f7",
            borderRadius: 2,
            p: 2,
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              style={{
                maxWidth: "100%",
                maxHeight: 300,
                objectFit: "contain",
              }}
            />
          ) : (
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              No Preview Available
            </Box>
          )}
        </Box>
    
        {/* Download */}
        {imageUrl && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Box
              component="a"
              href={imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 2,
                py: 1,
                border: "1px solid #1976d2",
                borderRadius: 1,
                fontSize: theme.typography.subtitle_xs,
                textDecoration: "none",
                color: "#1976d2",
                fontWeight: 500,
              }}
            >
              Download
            </Box>
          </Box>
        )}
      </Box>
    </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GrnViewReturnPage;
